import type { RoofHeelHeightOffsets, RoofSlope } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { assertIsNumber } from "common/utils/assertOfType";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { isDegeneratePolygonBSP, polygonBSPNew, splitPolygonPlaneBSP } from "geom/bsp";
import { buildFaceFromPolygonBSP } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { planeEval, planeNormal, polyLine2OnPlane } from "geom/plane";
import { planeBSPByPointAndNormal, planeBSPFromPlane } from "geom/planeBSP";
import { polyLine2Simplify } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3RemoveDuplicateNeighbors } from "geom/polyLine3";
import { pushXY } from "geom/utils";
import { NULL_HEEL_HEIGHT_OFFSETS } from "./roofHeelHeightOffsets";
import { getExtensionAtHeightFromBase } from "./roofSlopes";

interface GableProfileOptions {
  leftSideSlopes: RoofSlope[];
  rightSideSlopes: RoofSlope[];
  span: number;
  leftSpan?: number;
  rightSpan?: number;
  leftWallToRoofEdgeDistance: number;
  rightWallToRoofEdgeDistance: number;
  wallExtensionHeightLeft: number;
  wallExtensionHeightRight: number;
  truncatedHeight: number;
  truncated: boolean;
  closed: boolean;
  topOffset?: number;
  bottomOffset?: number;
  heelHeightOffsets?: RoofHeelHeightOffsets;
  roofType?: GableRoofType;
}

export function getGableProfile({
  leftSideSlopes,
  rightSideSlopes,
  span,
  leftSpan,
  rightSpan,
  leftWallToRoofEdgeDistance,
  rightWallToRoofEdgeDistance,
  wallExtensionHeightLeft,
  wallExtensionHeightRight,
  truncatedHeight,
  truncated,
  closed,
  bottomOffset = 0,
  topOffset = 0,
  heelHeightOffsets = NULL_HEEL_HEIGHT_OFFSETS,
  roofType = GableRoofType.DEFAULT,
}: GableProfileOptions): PolyLine2 {
  const points: number[] = [];

  let minX: number;
  if (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    assertIsNumber(leftSpan);
    minX = leftSpan;
  } else {
    minX = 0;
  }

  let maxX: number;
  if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
    assertIsNumber(rightSpan);
    maxX = rightSpan;
  } else {
    maxX = span;
  }

  const minHeelHeightOffset = Math.min(heelHeightOffsets.leftOffset, heelHeightOffsets.rightOffset);

  let maxY = Number.MAX_VALUE;
  if (truncated) {
    maxY = closed ? truncatedHeight - GABLE_ROOF_THICKNESS : truncatedHeight;
  }

  const pushPoint = (x: number, y: number) => {
    pushXY(points, x, Math.min(y, maxY));
  };

  if (closed) {
    pushPoint(Math.max(minX, leftWallToRoofEdgeDistance), bottomOffset + topOffset + minHeelHeightOffset);
    if (wallExtensionHeightLeft > 0) {
      pushPoint(
        Math.max(minX, leftWallToRoofEdgeDistance),
        wallExtensionHeightLeft + topOffset + heelHeightOffsets.leftOffset,
      );
    }
  } else if (roofType !== GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    pushPoint(Math.max(minX, 0), bottomOffset + topOffset + heelHeightOffsets.leftOffset);
  }

  if (closed || roofType !== GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    for (let i = 0, l = leftSideSlopes.length; i < l; i++) {
      const { endDist, endHeight } = leftSideSlopes[i];

      if (endDist < leftWallToRoofEdgeDistance - DIST_EPSILON) {
        continue;
      }

      if (truncated && endHeight > truncatedHeight) {
        pushPoint(
          Math.max(minX, getExtensionAtHeightFromBase(leftSideSlopes, truncatedHeight)),
          truncatedHeight + topOffset,
        );
        break;
      }

      pushPoint(Math.max(minX, endDist), endHeight + topOffset);
    }
  }

  if (closed || roofType !== GableRoofType.LEFT_SIDE_SHED_ROOF) {
    for (let l = rightSideSlopes.length, i = l - 1; i >= 0; i--) {
      const { endDist, endHeight } = rightSideSlopes[i];

      if (endDist < rightWallToRoofEdgeDistance - DIST_EPSILON) {
        continue;
      }

      if (truncated && endHeight > truncatedHeight) {
        pushPoint(
          Math.min(maxX, span - getExtensionAtHeightFromBase(rightSideSlopes, truncatedHeight)),
          truncatedHeight + topOffset,
        );
        continue;
      }

      pushPoint(Math.min(maxX, span - endDist), endHeight + topOffset);
    }
  }

  if (closed) {
    if (wallExtensionHeightRight > 0) {
      pushPoint(
        Math.min(maxX, span - rightWallToRoofEdgeDistance),
        wallExtensionHeightRight + topOffset + heelHeightOffsets.rightOffset,
      );
    }
    pushPoint(Math.min(maxX, span - rightWallToRoofEdgeDistance), bottomOffset + topOffset + minHeelHeightOffset);

    if (heelHeightOffsets.refOffset > 0) {
      pushPoint(
        Math.min(maxX, span - rightWallToRoofEdgeDistance),
        bottomOffset + topOffset - heelHeightOffsets.refOffset,
      );
      pushPoint(Math.max(minX, leftWallToRoofEdgeDistance), bottomOffset + topOffset - heelHeightOffsets.refOffset);
    }
    pushPoint(Math.max(minX, leftWallToRoofEdgeDistance), bottomOffset + topOffset + minHeelHeightOffset);
  } else if (roofType !== GableRoofType.LEFT_SIDE_SHED_ROOF) {
    pushPoint(Math.min(maxX, span), bottomOffset + topOffset + heelHeightOffsets.rightOffset);
  }

  return polyLine2Simplify(polyLine2New(points));
}

export function clipGableEndFace(face: Face | null, halfFace: Face | null): Face | null {
  if (!face) {
    return null;
  }

  if (!halfFace) {
    return face;
  }

  const { loops, plane } = face;
  if (loops.length === 0) {
    return null;
  }

  const exteriorVertices = polyLine3RemoveDuplicateNeighbors(polyLine2OnPlane(plane, loops[0]));
  if (exteriorVertices.length === 0) {
    return null;
  }

  const [nX, nY, nZ] = planeNormal(plane);
  const [oX, oY, oZ] = planeEval(plane, loops[0][0], loops[0][1]);
  const polygonPlane = planeBSPByPointAndNormal(oX, oY, oZ, nX, nY, nZ);

  const vertices = new Array<number>(exteriorVertices.length - 3);
  for (let i = 0, l = exteriorVertices.length - 3; i < l; i += 3) {
    vertices[i] = exteriorVertices[i];
    vertices[i + 1] = exteriorVertices[i + 1];
    vertices[i + 2] = exteriorVertices[i + 2];
  }

  const polygon = polygonBSPNew(0, vertices, polygonPlane);
  if (isDegeneratePolygonBSP(polygon)) {
    return null;
  }

  const { backPolygon } = splitPolygonPlaneBSP(planeBSPFromPlane(halfFace.plane), polygon, MULTIGABLE_BSP_EPSILON);

  if (isDegeneratePolygonBSP(backPolygon)) {
    return null;
  }

  return buildFaceFromPolygonBSP(backPolygon, plane);
}
